import type { LoggedUser, MedicalServiceType, RelyingPartyAdminType, RelyingPartyType } from '@/shared/lib/types/graphql';
import type { MetaDataType } from '@/shared/lib/types/tasks';
import { gql } from '@apollo/client';

export type AddCompanyResponse = {
  addRelyingParty: {
    status: string;
    relyingParty: PartialCompanyType;
  };
};

export const ADD_COMPANY = gql`
  mutation AddRelyingParty(
    $name: String!
    $adminsDetails: [RelyingPartyUsersInput!]
    $medicalServices: [MedicalServiceTypeEnum!]!
    $clientOrganizationId: String
    $clientStoreId: String
    $isMock: Boolean
  ) {
    addRelyingParty(
      name: $name
      adminsDetails: $adminsDetails
      medicalServices: $medicalServices
      clientOrganizationId: $clientOrganizationId
      clientStoreId: $clientStoreId
      isMock: $isMock
    ) {
      status
      relyingParty {
        id
        name
        createdAt
      }
    }
  }
`;

export type AddRelyingPartyMedicalServicesResponse = {
  addRelyingPartyMedicalServices: {
    status: string;
    relyingParty: {
      id: string;
      medicalServices: MedicalServiceType[];
    };
  };
};

export const ADD_RELYING_PARTY_MEDICAL_SERVICES = gql`
  mutation AddRelyingPartyMedicalServices($relyingPartyId: ID!, $medicalServices: [MedicalServiceTypeEnum!]!) {
    addRelyingPartyMedicalServices(relyingPartyId: $relyingPartyId, medicalServices: $medicalServices) {
      status
      relyingParty {
        id
        medicalServices {
          name
          shortName
          serviceType
          description
        }
      }
    }
  }
`;

export type GetAllCompaniesResponse = {
  relyingPartiesAll: {
    relyingParties: RelyingPartyType[];
    meta: MetaDataType;
  };
};

export const GET_ALL_COMPANIES = gql`
  query getRelyingPartiesAll(
    $pageNum: Int
    $pageSize: Int
    $filterOptions: RelyingPartyFilterOptionsInput
    $sortField: String
    $sortDirection: String
  ) {
    relyingPartiesAll(
      pageNum: $pageNum
      pageSize: $pageSize
      filterOptions: $filterOptions
      sortField: $sortField
      sortDirection: $sortDirection
    ) {
      relyingParties {
        name
        id
        createdAt
        goLiveDate
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type RolloutGroupCompany = {
  id: string;
  name: string;
  /** Server-resolved cohort. Nullable by design -- normalise with `toRolloutGroup` before use. */
  rolloutGroup?: string | null;
  isActive?: boolean;
};

export type GetRelyingPartiesRolloutGroupsResponse = {
  relyingPartiesAll: {
    relyingParties: RolloutGroupCompany[];
    meta: MetaDataType;
  };
};

/**
 * Every company plus its rollout cohort, unpaginated, for the rollout-group admin screen.
 *
 * Deliberately separate from GET_ALL_COMPANIES: that document is shared by eight call sites, and
 * adding fields there would inflate all of them.
 *
 * `pageNum`/`pageSize` are omitted on purpose -- leaving them off returns the full set, which is
 * what lets the grid filter and search entirely in the browser. Server-side pagination combined
 * with client-side filtering is the one combination that breaks, because `meta` would then
 * describe the unfiltered table and the grid would page wrong.
 *
 * `settings` is NOT selected: `rolloutGroup` is its own resolved field, and pulling the blob
 * instead would more than quintuple the response for the same rows and hand us an unvalidated
 * value. `includeInactive` is required or the list is quietly a subset.
 */
export const GET_RELYING_PARTIES_ROLLOUT_GROUPS = gql`
  query getRelyingPartiesRolloutGroups($filterOptions: RelyingPartyFilterOptionsInput) {
    relyingPartiesAll(filterOptions: $filterOptions) {
      relyingParties {
        id
        name
        rolloutGroup
        isActive
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type PartialCompanyType = {
  id: string;
  name: string;
  createdAt: string;
};

export type GetUserCompaniesResponse = {
  getUserRelyingParties: {
    relyingParties: RelyingPartyType[];
  };
};

export const GET_USER_COMPANIES = gql`
  query GetUserRelyingParties($hideCurrent: Boolean) {
    getUserRelyingParties(hideCurrent: $hideCurrent) {
      relyingParties {
        id
        name
        logo
        logoUrl
        orgHandle
        isMock
      }
    }
  }
`;

export type SwitchCompanyResponseType = {
  switchRelyingParty: {
    accessToken?: string;
    refreshToken?: string;
    otpId?: string;
    relyingPartyAdmin?: LoggedUser;
  };
};

export const SWITCH_COMPANY = gql`
  mutation SwitchRelyingParty($relyingPartyId: String!) {
    switchRelyingParty(relyingPartyId: $relyingPartyId) {
      accessToken
      refreshToken
      relyingPartyAdmin {
        id
        roleType
        presignedAvatarUrl
        isSecurityPinSet
        contactsRelyingPartyAdmins {
          contactType
          value
          relyingPartyAdminId
        }
        title
        businessName
        addressLine1
        addressLine2
        addressCity
        addressState
        addressZipcode
        phone
        alternateEmail
        hourlyRate
        supervisorEmail
        bankName
        bankRoutingNumber
        bankAccountNumber
        externalDialpadId
        settings
        preferences
        hasMultipleRelyingParties
        relyingParty {
          id
          name
          logoUrl
          orgHandle
          settings
          addressLine1
          addressLine2
          addressState
          addressCity
          phone
          email
          phone
          coverLetterBody
          medicalServices {
            name
            shortName
            serviceType
          }
          isCtaAccepted
          isMock
          primaryProvider {
            name
          }
        }
        roleType
        npiNumber
        techLicense
        user {
          id
          firstName
          lastName
          email
        }
      }
      otpId
    }
  }
`;

export type ConfirmSwitchCompanyResponseType = {
  confirmSwitchRelyingParty: {
    accessToken: string;
    refreshToken: string;
    relyingPartyAdmin: LoggedUser;
  };
};

export const CONFIRM_SWITCH_COMPANY = gql`
  mutation ConfirmSwitchRelyingParty($otpId: String!, $otpCode: String!) {
    confirmSwitchRelyingParty(otpId: $otpId, otpCode: $otpCode) {
      accessToken
      refreshToken
      relyingPartyAdmin {
        id
        roleType
        isSecurityPinSet
        contactsRelyingPartyAdmins {
          contactType
          value
          relyingPartyAdminId
        }
        title
        businessName
        addressLine1
        addressLine2
        addressCity
        addressState
        addressZipcode
        phone
        alternateEmail
        hourlyRate
        bankName
        bankRoutingNumber
        bankAccountNumber
        externalDialpadId
        settings
        preferences
        hasMultipleRelyingParties
        relyingParty {
          id
          name
          logoUrl
          orgHandle
          settings
          medicalServices {
            name
            shortName
            serviceType
          }
          isCtaAccepted
          isMock
        }
        roleType
        user {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

export type GetCompanyMetaDataResponse = {
  relyingPartyAdminsByRelyingParty: {
    relyingPartyAdmins: RelyingPartyAdminType[];
    meta: MetaDataType;
  };
};

export const RELYING_PARTY_ADMINS_BY_RELYING_PARTY = gql`
  query RelyingPartyAdminsByRelyingParty($relyingPartyId: ID!, $pageNum: Int, $pageSize: Int, $roleType: AdminRoleTypeEnum) {
    relyingPartyAdminsByRelyingParty(relyingPartyId: $relyingPartyId, pageNum: $pageNum, pageSize: $pageSize, roleType: $roleType) {
      relyingPartyAdmins {
        id
        roleType
        title
        settings
        isRpmPrimary
        isObserver
        isActive
        user {
          firstName
          lastName
          email
        }
      }
      meta {
        totalCount
        totalPages
      }
    }
  }
`;

export type UpdateRelyingPartyAdminRpmPrimaryResponse = {
  updateRelyingPartyAdminRpmPrimary: {
    status: string;
    message: string;
  };
};

export const UPDATE_RELYING_PARTY_ADMIN_RPM_PRIMARY = gql`
  mutation UpdateRelyingPartyAdminRpmPrimary($relyingPartyAdminId: ID!, $relyingPartyId: ID!, $isRpmPrimary: Boolean!) {
    updateRelyingPartyAdminRpmPrimary(
      relyingPartyAdminId: $relyingPartyAdminId
      relyingPartyId: $relyingPartyId
      isRpmPrimary: $isRpmPrimary
    ) {
      status
      message
    }
  }
`;

export type DeactivateCompanyResponseType = {
  deactivateRelyingParty: {
    status: string;
  };
};

export const DEACTIVATE_COMPANY = gql`
  mutation DeactivateRelyingParty($relyingPartyId: ID!) {
    deactivateRelyingParty(relyingPartyId: $relyingPartyId) {
      status
    }
  }
`;

export type ActivateCompanyResponseType = {
  activateRelyingParty: {
    status: string;
  };
};

export const ACTIVATE_COMPANY = gql`
  mutation ActivateRelyingParty($relyingPartyId: ID!) {
    activateRelyingParty(relyingPartyId: $relyingPartyId) {
      status
      relyingParty {
        id
        name
        isActive
      }
    }
  }
`;

/** perPharmacyOnly flags are counted here but turned on one pharmacy at a time, never in bulk. */
export type FeatureFlagSummary = { default: boolean; on: number; off: number; newPharmacies: boolean; perPharmacyOnly?: boolean };

export type GetFeatureFlagsSummaryResponse = {
  /**
   * Each AdditionalFeatures flag, how many companies have it on or off (unset = its default),
   * and whether a new RPM company starts with it on.
   */
  featureFlagsSummary: Record<string, FeatureFlagSummary>;
};

export const GET_FEATURE_FLAGS_SUMMARY = gql`
  query GetFeatureFlagsSummary {
    featureFlagsSummary
  }
`;

export type UpdateAllRelyingPartiesFeatureFlagsResponse = {
  updateAllRelyingPartiesFeatureFlags: { updatedCount: number };
};

/** Writes the given AdditionalFeatures flags into every company's settings. */
export const UPDATE_ALL_RELYING_PARTIES_FEATURE_FLAGS = gql`
  mutation UpdateAllRelyingPartiesFeatureFlags($flags: JSON!) {
    updateAllRelyingPartiesFeatureFlags(flags: $flags) {
      updatedCount
    }
  }
`;

/** One AdditionalFeatures flag of a company; value is null when the company doesn't have it. */
/** value is true/false, or null when unset; a non-editable flag holds some other stored value (e.g. text). */
export type RelyingPartyFeatureFlag = {
  key: string;
  value: boolean | string | number | null;
  derivedManually: boolean;
  editable: boolean;
};

export type GetRelyingPartyFeatureFlagsResponse = { relyingPartyFeatureFlags: RelyingPartyFeatureFlag[] };

/** A company's flags: code flags first, then ones typed into its settings by hand (derivedManually). */
export const GET_RELYING_PARTY_FEATURE_FLAGS = gql`
  query GetRelyingPartyFeatureFlags($relyingPartyId: ID!) {
    relyingPartyFeatureFlags(relyingPartyId: $relyingPartyId)
  }
`;

export type UpdateRelyingPartyFeatureFlagsResponse = {
  updateRelyingPartyFeatureFlags: { flags: RelyingPartyFeatureFlag[] };
};

/** Writes the given flags into one company's settings; nothing else in them changes. */
export const UPDATE_RELYING_PARTY_FEATURE_FLAGS = gql`
  mutation UpdateRelyingPartyFeatureFlags($relyingPartyId: ID!, $flags: JSON!) {
    updateRelyingPartyFeatureFlags(relyingPartyId: $relyingPartyId, flags: $flags) {
      flags
    }
  }
`;

export type UpdateNewPharmacyFeatureFlagsResponse = {
  updateNewPharmacyFeatureFlags: { flags: Record<string, boolean> };
};

/** Sets the AdditionalFeatures flags new RPM companies start with; existing companies are untouched. */
export const UPDATE_NEW_PHARMACY_FEATURE_FLAGS = gql`
  mutation UpdateNewPharmacyFeatureFlags($flags: JSON!) {
    updateNewPharmacyFeatureFlags(flags: $flags) {
      flags
    }
  }
`;
